/**
 * Round economy — money awarded per round outcome and per kill.
 * Adapted from CS2 MR12 format.
 */

export const ECONOMY = {
  /** Initial round budget (pistol round). */
  startingMoney: 800,

  /**
   * Warmup (searching-for-opponent) practice budget. A lone player waiting
   * for a match gets this to freely test guns + grenades. Intentionally
   * above `maxWallet` — it's set directly (never via a clamped award) and
   * is wiped back to `startingMoney` the instant the real match begins.
   */
  warmupMoney: 20000,

  /** Awarded to winning side. */
  roundWin: 3250,

  /** Loss bonus by consecutive losing streak (index = streak count, capped at 4). */
  lossBonus: [1400, 1900, 2400, 2900, 3400] as const,

  /** Kill rewards by weapon class. */
  killReward: {
    knife:   1500,
    pistol:  300,
    smg:     600,
    rifle:   300,
    sniper:  100,
    grenade: 300,
  } as const,

  /** Headshot bonus on top of kill reward (CS2 has none — kept 0 for parity). */
  headshotBonus: 0,

  /** Sprint 6 — CS:GO wallet cap. Awards beyond this are clamped. */
  maxWallet: 16000,
} as const;

/* ─────────────────────────────────────────────────────────────────
   PLATFORM FEE STRUCTURE
   These are the fees the platform takes from the pot, NOT the
   round-economy money above. Configured matching contract defaults.
   ───────────────────────────────────────────────────────────────── */
export const FEES = {
  /** 1v1 match rake — basis points (1000 = 10.00%). */
  matchFeeBps:      1000,
  /** Tournament rake — basis points (1200 = 12.00%). */
  tournamentFeeBps: 1200,
  /** Withdrawal fee in USDT base units (currently 0). */
  withdrawalFeeUsdt: 0,
} as const;

/* ─────────────────────────────────────────────────────────────────
   STAKE GUARDRAILS
   Real-money 1v1 stakes are whole-dollar amounts in [MIN_STAKE, MAX_STAKE].
   Single source for BOTH the open matchmaking queues (game-server rooms) and
   custom invites (API), so a tampered client can't force a fractional or
   out-of-range escrow lock from either entry point.
   ───────────────────────────────────────────────────────────────── */
export const MIN_STAKE = 1;
export const MAX_STAKE = 10_000;

/** True if `raw` is a whole-dollar stake within [MIN_STAKE, MAX_STAKE]. */
export function isValidStake(raw: unknown): boolean {
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isInteger(n) && n >= MIN_STAKE && n <= MAX_STAKE;
}

/**
 * Coerce a client-supplied stake to a safe whole-dollar value in
 * [MIN_STAKE, MAX_STAKE]. Non-numeric, fractional, or out-of-range inputs are
 * rounded and clamped to the nearest bound, so a paid room can never be created
 * with a fractional or out-of-range stake (an absent stake → MIN_STAKE, the
 * previous default).
 */
export function clampStake(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return MIN_STAKE;
  return Math.min(MAX_STAKE, Math.max(MIN_STAKE, Math.round(n)));
}

/* ─────────────────────────────────────────────────────────────────
   ARMOR — buyable equipment (vest / vest+helmet), CS:GO prices.
   Not weapons: bought via the same buy_weapon message but routed to
   armor state on the server (never set as the equipped weaponId).
   ───────────────────────────────────────────────────────────────── */

/** Full armor value granted by a fresh kevlar purchase. */
export const ARMOR_MAX = 100;

export interface ArmorItem {
  /** Buy id (matches the buy_weapon message + owned tracking). */
  id: string;
  name: string;
  cost: number;
  /** Whether this purchase also grants head protection. */
  givesHelmet: boolean;
  desc: string;
}

/** The two CS:GO armor buys. */
export const ARMOR_ITEMS: readonly ArmorItem[] = [
  { id: "vest",       name: "Kevlar Vest",     cost: 650,  givesHelmet: false, desc: "100 armor — absorbs body damage." },
  { id: "vesthelmet", name: "Kevlar + Helmet", cost: 1000, givesHelmet: true,  desc: "100 armor + helmet — protects the head too." },
] as const;

/** Lookup an armor item by buy id (undefined if it isn't armor). */
export function getArmorItem(id: string): ArmorItem | undefined {
  return ARMOR_ITEMS.find((a) => a.id === id);
}

/**
 * CS:GO helmet-only top-up price. When a player already has full armor and
 * no helmet, buying "Kevlar + Helmet" only charges for the helmet ($350)
 * instead of re-buying the vest they already have.
 */
export const HELMET_ONLY_COST = 350;

/**
 * Effective cost to buy an armor item given the player's current armor value
 * and helmet state — CS:GO rules. Returns `null` when the purchase would be a
 * no-op (already at full protection for that item), so one call both prices
 * the buy and detects the "already owned" case.
 *
 *   • Kevlar Vest:    full armor already → null; else $650 (refills to 100).
 *   • Kevlar + Helmet:
 *       – full armor + helmet  → null (already maxed)
 *       – full armor, no helmet → HELMET_ONLY_COST ($350 — just the helmet)
 *       – otherwise             → item.cost ($1000 — refills armor + helmet)
 */
export function armorPurchaseCost(item: ArmorItem, armor: number, helmet: boolean): number | null {
  const fullArmor = armor >= ARMOR_MAX;
  if (item.givesHelmet) {
    if (fullArmor && helmet) return null;
    if (fullArmor) return HELMET_ONLY_COST;
    return item.cost;
  }
  return fullArmor ? null : item.cost;
}

/** Compute lossBonus given streak count (clamped). */
export function lossBonusFor(streak: number): number {
  const idx = Math.max(0, Math.min(streak, ECONOMY.lossBonus.length - 1));
  return ECONOMY.lossBonus[idx];
}

/** Compute platform fee on a pot, in same units as pot. */
export function platformFee(pot: number, bps: number = FEES.matchFeeBps): number {
  return Math.floor((pot * bps) / 10_000);
}

/**
 * Round format constants — short MR7 match.
 *
 * MR7 = "max rounds 7 per half" → halftime after 7 rounds, first to 8
 * wins. If the score is 7-7 after the regulation 14 rounds, overtime
 * (MR3 = max 6 OT rounds, first to 4 wins — declared but not yet wired).
 */
export const MATCH_FORMAT = {
  /** Rounds per half (so a tie at this score triggers OT). */
  halfRounds: 7,
  /** Wins required to take the match in regulation. */
  roundsToWin: 8,
  /** Round at which sides swap (after this many rounds played). */
  halftimeAtRound: 7,
  /** Halftime side-swap cinematic: after the swap the match holds on a
   *  `halftime` phase for this long (each player sees their own mannequin
   *  posed on its NEW side + the new team title) before the second half's
   *  first buy phase begins. */
  halftimeSeconds: 10,
  /** Pistol round numbers (1-based): round 1 + first round of the 2nd half. */
  pistolRoundNumbers: [1, 8] as const,
  buyPhaseSeconds: 15,
  /** Pre-buy cinematic "VS" intro shown once, right after an opponent is
   *  found and before the first buy phase: the two fighters stand on the mid
   *  platform while the random side draw is revealed and a timer counts down.
   *  Only runs at match start, not between rounds. */
  introSeconds: 10,
  roundLengthSeconds: 115,
  roundEndSeconds: 5,
  /** After the match-winning kill, keep grenade fire / smoke / explosions
   *  (and their sounds) alive — and hold back the result overlay — for this
   *  many seconds so the aftermath plays out instead of cutting to silence. */
  matchEndGraceSeconds: 4,
  overtimeRounds: 6,    // MR3 sudden death after a 7-7 regulation tie
  overtimeRoundsToWin: 4,
} as const;
